import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Settings, MessageCircle, AlertCircle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface TelegramBotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TelegramSettings {
  id?: string;
  bot_token: string;
  chat_id: string;
  is_active: boolean;
  bot_username?: string;
  last_checked_at?: string;
}

export function TelegramBotDialog({ open, onOpenChange }: TelegramBotDialogProps) {
  const [settings, setSettings] = useState<TelegramSettings>({
    bot_token: "",
    chat_id: "",
    is_active: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [botStatus, setBotStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('telegram_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data);
        setBotStatus(data.is_active ? 'connected' : 'unknown');
      }
    } catch (error) {
      console.error('Error loading telegram settings:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել Bot-ի կարգավորումները",
        variant: "destructive",
      });
    }
  };

  const testBotConnection = async () => {
    if (!settings.bot_token) {
      toast({
        title: "Սխալ",
        description: "Նշեք Bot Token-ը",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    try {
      const response = await supabase.functions.invoke('test-telegram-bot', {
        body: { 
          botToken: settings.bot_token,
          chatId: settings.chat_id 
        }
      });

      if (response.error) throw response.error;

      const { data } = response;
      if (data.success) {
        setBotStatus('connected');
        setSettings(prev => ({ ...prev, bot_username: data.botUsername }));
        toast({
          title: "Կապակցված",
          description: `Bot-ը աշխատում է: @${data.botUsername}`,
        });
      } else {
        setBotStatus('error');
        toast({
          title: "Սխալ",
          description: data.error || "Bot-ի հետ կապ չի հաստատվում",
          variant: "destructive",
        });
      }
    } catch (error) {
      setBotStatus('error');
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ստուգել Bot-ի կապը",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const saveSettings = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('telegram_settings')
        .upsert({
          ...settings,
          last_checked_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast({
        title: "Պահպանված",
        description: "Telegram Bot-ի կարգավորումները պահպանվեցին",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving telegram settings:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց պահպանել կարգավորումները",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestMessage = async () => {
    if (!settings.bot_token || !settings.chat_id) {
      toast({
        title: "Սխալ",
        description: "Նշեք Bot Token-ը և Chat ID-ն",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    try {
      const response = await supabase.functions.invoke('send-telegram-message', {
        body: {
          message: "🤖 Test հաղորդագրություն Global IT Academy Admin Panel-ից",
          recipientType: 'custom',
          customChatId: settings.chat_id
        }
      });

      if (response.error) throw response.error;

      toast({
        title: "Ուղարկված",
        description: "Test հաղորդագրությունը ուղարկվեց",
      });
    } catch (error) {
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ուղարկել test հաղորդագրությունը",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Telegram Bot կարգավորումներ
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Bot Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {botStatus === 'connected' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                {botStatus === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                {botStatus === 'unknown' && <MessageCircle className="h-4 w-4 text-muted-foreground" />}
                Bot կարգավիճակ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                {botStatus === 'connected' && settings.bot_username && (
                  <p className="text-green-600">Կապակցված: @{settings.bot_username}</p>
                )}
                {botStatus === 'error' && (
                  <p className="text-red-600">Կապի խնդիր կա</p>
                )}
                {botStatus === 'unknown' && (
                  <p className="text-muted-foreground">Կապի կարգավիճակը անհայտ է</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Settings Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bot_token">Bot Token</Label>
              <Input
                id="bot_token"
                type="password"
                placeholder="1234567890:AABBCCDDEEFFGGHHIIJJKKLLMMNNOOPPqq"
                value={settings.bot_token}
                onChange={(e) => setSettings(prev => ({ ...prev, bot_token: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                @BotFather-ից ստացված token-ը
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chat_id">Chat ID</Label>
              <Input
                id="chat_id"
                placeholder="-1001234567890"
                value={settings.chat_id}
                onChange={(e) => setSettings(prev => ({ ...prev, chat_id: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Խմբի կամ ալիքի Chat ID-ն, որտեղ ուղարկվելու են հաղորդագրությունները
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={settings.is_active}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Bot-ը ակտիվ է</Label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={testBotConnection}
              disabled={isTesting || !settings.bot_token}
            >
              {isTesting ? "Ստուգվում է..." : "Ստուգել կապը"}
            </Button>

            <Button
              variant="outline"
              onClick={sendTestMessage}
              disabled={isTesting || !settings.bot_token || !settings.chat_id}
            >
              {isTesting ? "Ուղարկվում է..." : "Test հաղորդագրություն"}
            </Button>
          </div>

          {/* Save/Cancel */}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Չեղարկել
            </Button>
            <Button onClick={saveSettings} disabled={isLoading}>
              {isLoading ? "Պահպանվում է..." : "Պահպանել"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}